"use client";

import { ArrowRight, Clock, Shield, ShieldCheck, Umbrella as UmbrellaIcon, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Guarantee() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const banner = useRevealOnScroll<HTMLDivElement>();
  const cta = useRevealOnScroll<HTMLDivElement>();
  const badges = [{
    icon: Shield,
    title: "Certyfikowana Technologia",
    description: "Stosujemy wyłącznie atestowane membrany poliuretanowe, które gwarantują najwyższą szczelność i trwałość na lata.",
    label: "Atesty PZH"
  }, {
    icon: UserCheck,
    title: "Doświadczony Zespół",
    description: "Nasi specjaliści posiadają wieloletnie doświadczenie w natryskowej hydroizolacji dachów i obiektów przemysłowych.",
    label: "Eksperci"
  }, {
    icon: Clock,
    title: "Terminowość Realizacji",
    description: "Szanujemy Twój czas. Każdy projekt realizujemy zgodnie z ustalonym harmonogramem, bez zbędnych opóźnień.",
    label: "Punktualność"
  }, {
    icon: UmbrellaIcon,
    title: "Pełna Szczelność",
    description: "Nasza bezspoinowa metoda natryskowa eliminuje ryzyko przecieków, tworząc jednolitą barierę wodoodporną.",
    label: "Gwarancja 100%"
  }];
  const certifications = [{
    label: "Certyfikat Jakości"
  }, {
    label: "Normy ISO"
  }, {
    label: "Gwarancja Producenta"
  }];
  return <section data-section-id="673" id="gwarancja-jakości" className="relative overflow-hidden bg-slate-950 py-32 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[80%] opacity-40" style={{
      background: "radial-gradient(ellipse 60% 70% at 50% 100%, hsla(200, 95%, 45%, 0.25), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-sm font-semibold tracking-[0.2em] text-blue-500 uppercase font-default">
            Nasza Obietnica
          </p>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-[56px] lg:leading-[1.05] font-bold text-white font-default tracking-tight">
            Profesjonalna hydroizolacja z gwarancją trwałości.
          </h2>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto font-default">
            HydroRoof to synonim niezawodności. Dostarczamy zaawansowane rozwiązania hydroizolacyjne, które chronią Twój dach przed wodą, UV i zmiennymi warunkami atmosferycznymi.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {badges.map((badge, i) => {
          const Icon = badge.icon;
          return <Card data-index={i} key={i} className="bg-slate-900 border border-slate-800 rounded-lg p-8 min-h-[280px] flex flex-col items-center text-center shadow-lg hover:border-blue-500/40 transition-colors duration-300">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-lg blur-xl opacity-40" style={{
                background: "hsl(200 95% 45% / 0.15)"
              }} aria-hidden="true" />
                  <div className="relative flex items-center justify-center w-[72px] h-[72px] rounded-lg border-2 border-slate-700 bg-slate-950">
                    <Icon className="w-9 h-9 text-blue-500" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white font-default mb-3">
                  {badge.title}
                </h3>
                <p className="text-sm text-slate-400 font-default leading-relaxed mb-6 flex-1">
                  {badge.description}
                </p>
                <Badge variant="outline" className="bg-blue-500/10 border-blue-500/30 text-blue-400 font-medium tracking-wide uppercase text-[10px] px-3 py-1">
                  {badge.label}
                </Badge>
              </Card>;
        })}
        </div>

        {}
        <div ref={banner.ref} className={`mt-16 rounded-lg border border-slate-800 bg-slate-900 p-8 lg:p-12 transition-all duration-700 ease-out ${banner.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6">
              {certifications.map((cert, i) => <div data-index={i} key={i} className="flex items-center gap-2 px-4 py-3 rounded-lg border border-slate-700 bg-slate-950">
                  <ShieldCheck className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-slate-300 font-default tracking-wide">
                    {cert.label}
                  </span>
                </div>)}
            </div>

            {}
            <div className="text-center lg:text-right">
              <p className="text-2xl lg:text-3xl font-semibold text-white font-default leading-tight">
                Setki udanych realizacji.{" "}
                <span className="text-blue-500">Zero przecieków.</span>
              </p>
            </div>
          </div>
        </div>

        {}
        <div ref={cta.ref} className={`mt-12 text-center transition-all duration-700 ease-out ${cta.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="ghost" size="lg" className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10 font-semibold text-base">
            <Link to="/ContactPage">
              Sprawdź naszą pełną ofertę
              <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}